import React, { useState } from 'react';
import Breadcrumb from '../components/Breadcrumb';
import { Box, Button, CircularProgress, Input, Link, Typography, Sheet, Stack } from '@mui/joy';
import PostAddRoundedIcon from '@mui/icons-material/PostAddRounded';
import tempoApi from '../api/tempo';
import NewInvoiceTable from '../components/NewInvoiceTable';
import { IConflictLog, IMappedLog, IMappedLogInvoice, IWorklog } from '../interface/IWorklog';
import { calculateNewInvoiceTotalAmount, deutschNumberFormatter, getTimeLoggedStringFormat, secondsToHms } from '../helper/utils';
import ConflictIssuesModal from '../components/ConflictIssuesModal';
import invoiceApi from '../api/invoice';
import { useNavigate } from 'react-router-dom';
import offerApi from '../api/offer';
import { IOffer } from '../interface/IOffer';
import InvoiceSubmitModal from '../components/InvoiceSubmitModal';
import dayjs from 'dayjs';
import { useToast } from '../helper/ToastProvider';
import { styled } from '@mui/joy/styles';
type Props = {};

const Item = styled(Sheet)(({ theme }) => ({
  ...theme.typography['body-sm'],
  textAlign: 'center',
  fontWeight: theme.fontWeight.md,
  color: theme.vars.palette.text.secondary,
  border: '1px solid',
  borderColor: theme.palette.divider,
  padding: theme.spacing(1),
  borderRadius: theme.radius.md,
}));

const NewInvoice = (props: Props) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string | null>(null);
  const [offers, setOffers] = useState<IOffer[]>();
  const [worklogs, setWorklogs] = useState<IWorklog[]>();
  const [mappedLogs, setMappedLogs] = useState<IMappedLog[]>();
  const [mappedLogInvoices, setMappedLogInvoices] = useState<IMappedLogInvoice[]>([]);

  const [conflictIssues, setConflictIssues] = useState<any>();
  const [openConflictIssueModal, setOpenConflictIssueModal] = useState<boolean>(false);
  const [totalInvoiceTime, setTotalInvoiceTime] = useState<number>(0);
  const [openSubmitModal, setOpenSubmitModal] = useState<boolean>(false);
  const { showToast } = useToast();

  const mapLogs = (log: IWorklog, t: IMappedLog[], total: [number], _offers: IOffer[]) => {
    const offerIndex = t.findIndex((x) => x.offerId === log.issue.epic.offerId);
    const {
      issue: {
        epic: { offerId, ...epicRest },
        ...restIssue
      },
      ...rest
    } = log;
    const offer = _offers?.find((x) => x.offerId === offerId);
    const offerPosition = offer?.offerPositions?.find((x) => x.position == log.issue.epic.position);
    let offerPositionContractType = offerPosition?.contractType || null;
    let isPeriodicFixedPrice = null;
    if (offerPositionContractType && offerPositionContractType == 'periodicFixedPrice') {
      if (log.description == 'periodicFixed') {
        isPeriodicFixedPrice = 'periodicFixed';
      } else {
        isPeriodicFixedPrice = 'notPeriodicFixed';
      }
    }
    if (offerId !== 'p7') total[0] = total[0] + rest.timeLogged;
    const timeLogged = isPeriodicFixedPrice == 'periodicFixed' ? 0 : rest.timeLogged;
    const timeInvoiced =
      isPeriodicFixedPrice == 'periodicFixed'
        ? rest.timeLogged
        : isPeriodicFixedPrice === 'notPeriodicFixed' || offerId === 'p7'
          ? 0
          : rest.timeLogged;
    const timeInvoicedSplit =
      isPeriodicFixedPrice == 'periodicFixed'
        ? secondsToHms(rest.timeLogged)
        : isPeriodicFixedPrice === 'notPeriodicFixed' || offerId === 'p7'
          ? { h: 0, m: 0 }
          : secondsToHms(rest.timeLogged);
    if (offerIndex < 0) {
      const toAdd = {
        offerId,
        checked: false,
        book: false,
        offer: offer,
        epics: [
          {
            ...epicRest,
            contractType: offerPositionContractType || '-',
            logs: [
              {
                ...rest,
                issue: restIssue,
                timeLogged: timeLogged,
                timeInvoiced: timeInvoiced,
                timeInvoicedSplit: timeInvoicedSplit,
              },
            ],
          },
        ],
      };
      t.push(toAdd);
    } else {
      const epicIndex = t[offerIndex].epics.findIndex((x) => x.id === log.issue.epic.id);
      // const epicIndex = t[offerIndex].epics.findIndex((x) => x.summary == log.issue.epic.summary);
      if (epicIndex < 0) {
        t[offerIndex].epics.push({
          ...epicRest,
          contractType: offerPositionContractType || '-',
          logs: [
            {
              ...rest,
              issue: restIssue,
              timeLogged: timeLogged,
              timeInvoiced: timeInvoiced,
              timeInvoicedSplit: timeInvoicedSplit,
            },
          ],
        });
      } else {
        t[offerIndex].epics[epicIndex].logs.push({
          ...rest,
          issue: restIssue,
          timeLogged: timeLogged,
          timeInvoiced: timeInvoiced,
          timeInvoicedSplit: timeInvoicedSplit,
        });
      }
    }
  };

  const getWorklogs = async () => {
    try {
      if (!fromDate || !toDate) return;
      setLoading(true);
      const res = await tempoApi.getWorklogsFromToDate(fromDate as string, toDate as string);
      let _offers: IOffer[] = res.data.offers;
      let _worklogs = res.data.worklogs;

      setWorklogs(_worklogs);
      setOffers(_offers);

      var t: IMappedLog[] = [];
      const conflicts: IConflictLog[] = [];
      let total: [number] = [0];
      _worklogs.forEach((log: IWorklog, index: number) => {
        if (log == null || log.issue == null || log.issue.epic == null) {
          console.log('Null issue or epic for log: ', log);
          return;
        }
        let isConflict = false;
        let isPeriodicExpenseConflict = false;
        let offerOfIssue = _offers?.find((x) => x.offerId == log.issue.epic.offerId);
        const offerPosition = offerOfIssue?.offerPositions?.find((x) => x.position == log.issue.epic.position);
        let offerPositionContractType = offerPosition?.contractType || null;
        if (offerPositionContractType && offerPositionContractType == 'periodicFixedPrice' && log.description != 'periodicFixed') {
          isPeriodicExpenseConflict = true;
        }
        if (!log.issue.epic.epic || log.issue.status.toLowerCase() == 'backlog' || isPeriodicExpenseConflict) {
          isConflict = true;
          let conflictIndex = conflicts.findIndex((conflict) => conflict.id == log.issue.id);
          if (conflictIndex < 0) {
            let newConflict: IConflictLog;
            let conflictReason = '';
            if (!log.issue.epic.epic && log.issue.status.toLowerCase() == 'backlog') {
              conflictReason = 'Both';
            } else if (!log.issue.epic.epic) {
              conflictReason = 'Parent';
            } else if (log.issue.status.toLowerCase() == 'backlog') {
              conflictReason = 'Status';
            } else if (isPeriodicExpenseConflict) {
              conflictReason = 'Logs in Fixed Price';
            }
            newConflict = { ...log.issue, workLogs: [log], conflictReason };
            conflicts.push(newConflict);
          } else {
            conflicts[conflictIndex].workLogs.push(log);
          }
        }
        if (isConflict == true) return;
        mapLogs(log, t, total, _offers);
      });
      setTotalInvoiceTime(total[0]);
      setConflictIssues(conflicts.sort((a, b) => (a.key < b.key ? -1 : 1)));
      setMappedLogs(t.sort((a, b) => (a.offerId < b.offerId ? -1 : 1)));
      setLoading(false);
      showToast({
        message: 'Logs Fetch Success',
        severity: 'success',
      });
    } catch (e) {
      console.error(e);
      setConflictIssues([]);
      setMappedLogs([]);
      setLoading(false);
      showToast({
        message: 'Logs Fetch Failed',
        severity: 'danger',
      });
    }
  };

  const updateHour = (newHour: number, logIndex: number, epicIndex: number, offerIndex: number) => {
    const temp = [...mappedLogs!];
    temp[offerIndex].epics[epicIndex].logs[logIndex].timeInvoicedSplit.h = newHour;
    let newTimeInvoiced = (newHour || 0) * 60 * 60 + (temp[offerIndex].epics[epicIndex].logs[logIndex].timeInvoicedSplit.m || 0) * 60;
    let t = totalInvoiceTime;
    t = t - temp[offerIndex].epics[epicIndex].logs[logIndex].timeInvoiced + newTimeInvoiced;
    temp[offerIndex].epics[epicIndex].logs[logIndex].timeInvoiced = newTimeInvoiced;
    setTotalInvoiceTime(t);
    setMappedLogs(temp);
  };

  const updateMinute = (newMinute: number, logIndex: number, epicIndex: number, offerIndex: number) => {
    const temp = [...mappedLogs!];
    temp[offerIndex].epics[epicIndex].logs[logIndex].timeInvoicedSplit.m = newMinute;
    let newTimeInvoiced = (temp[offerIndex].epics[epicIndex].logs[logIndex].timeInvoicedSplit.h || 0) * 60 * 60 + (newMinute || 0) * 60;
    let t = totalInvoiceTime;
    t = t - temp[offerIndex].epics[epicIndex].logs[logIndex].timeInvoiced + newTimeInvoiced;
    temp[offerIndex].epics[epicIndex].logs[logIndex].timeInvoiced = newTimeInvoiced;
    setTotalInvoiceTime(t);
    setMappedLogs(temp);
  };

  const updateOfferChecked = (checked: boolean, offerIndex: number) => {
    const temp = [...mappedLogs!];
    temp[offerIndex].checked = checked;
    if (checked) {
      temp[offerIndex].book = false;
      temp[offerIndex].invoiceFrom = fromDate;
      temp[offerIndex].invoiceTo = toDate;
    } else {
      temp[offerIndex].invoiceFrom = null;
      temp[offerIndex].invoiceTo = null;
    }
    setMappedLogs(temp);
  };

  const updateOfferBook = (checked: boolean, offerIndex: number) => {
    const temp = [...mappedLogs!];
    temp[offerIndex].book = checked;
    if (checked) {
      temp[offerIndex].checked = false;
      temp[offerIndex].invoiceFrom = null;
      temp[offerIndex].invoiceTo = null;
    }
    setMappedLogs(temp);
  };

  const closeConflictIssueModal = () => setOpenConflictIssueModal(false);

  const onSubmit = async () => {
    try {
      setSubmitting(true);
      await invoiceApi.createInvoice({
        fromDate: fromDate,
        toDate: toDate,
        offers: mappedLogInvoices,
      });
      navigate('/invoices');
      setSubmitting(false);
      closeInvoiceSubmitModal();
      showToast({
        message: 'Invoice Create Success',
        severity: 'success',
      });
    } catch (e) {
      console.error(e);
      setSubmitting(false);
      showToast({
        message: 'Invoice Create Failed',
        severity: 'danger',
      });
    }
  };

  const onCreate = async () => {
    try {
      // setSubmitting(true);
      const data: IMappedLogInvoice[] = mappedLogs
        ?.filter((l) => l.checked || l.book)
        .map((l) => ({
          ...l,
          dueDate: dayjs().add(30, 'day').format('YYYY-MM-DD'),
          invoiceDate: dayjs().format('YYYY-MM-DD'),
          sendEmail: false,
        })) as IMappedLogInvoice[];
      // await invoiceApi.createInvoice({
      //   fromDate: fromDate,
      //   toDate: toDate,
      //   invoiceDate: invoiceDate,
      //   offers: data,
      // });
      if (data.length > 0) {
        setMappedLogInvoices(data);
        setOpenSubmitModal(true);
      } else {
      }
      // navigate('/invoices');
      // setSubmitting(false);
    } catch (e) {
      // setSubmitting(false);
    }
  };

  const updateOfferDate = async (newDate: string, type: string, offerIndex: number) => {
    const temp = [...mappedLogs!];
    if (type === 'from') {
      temp[offerIndex].invoiceFrom = newDate;
    } else if (type === 'to') {
      temp[offerIndex].invoiceTo = newDate;
    }
    setMappedLogs(temp);
  };

  const onConflictIgnore = (issueId: string) => {
    let tempConflictIssues = [...conflictIssues];
    let ignoreConflictIssueIndex = tempConflictIssues.findIndex((ci: IConflictLog) => ci.id == issueId);
    let tempMappedLogs: IMappedLog[] = [...(mappedLogs || [])];
    if (ignoreConflictIssueIndex > -1) {
      let total: [number] = [totalInvoiceTime];
      let ignoreConflictIssue = tempConflictIssues[ignoreConflictIssueIndex];
      ignoreConflictIssue.workLogs.forEach((log: IWorklog, index: number) => {
        mapLogs(log, tempMappedLogs, total, offers!);
      });
      tempConflictIssues.splice(ignoreConflictIssueIndex, 1);
      setTotalInvoiceTime(total[0]);
      setConflictIssues(tempConflictIssues);
      setMappedLogs(tempMappedLogs);
    }
  };

  const closeInvoiceSubmitModal = () => {
    setOpenSubmitModal(false);
  };

  const onChangeInvoice = <T extends keyof IMappedLogInvoice>(offerId: string, key: T, value: IMappedLogInvoice[T]) => {
    let temp = [...mappedLogInvoices!];
    let o = temp.find((x) => x.offerId == offerId);
    if (o) {
      o[key] = value;
      if (key == 'invoiceDate') {
        o.dueDate = dayjs(value as string)
          .add(30, 'days')
          .format('YYYY-MM-DD');
      }
    }
    setMappedLogInvoices(temp);
  };

  const generateExcel = async () => {
    try {
      const response = await invoiceApi.downloadNewInvoiceExcelFile(mappedLogs!);
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition.split(';')[1].split('=')[1].replace(/"/g, '');
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading Excel:', error);
    }
  };

  return (
    <>
      <Breadcrumb crumb={['Invoices', 'New Invoice']} />
      <Box
        sx={{
          display: 'flex',
          mb: 1,
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'start', sm: 'center' },
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Typography level='h2' component='h1'>
          New Invoice
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          mb: 1,
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'start', sm: 'center' },
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            mb: 1,
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'start', sm: 'flex-end' },
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography level='title-sm' component='label'>
              From Date
            </Typography>
            <Input
              onChange={(e) => setFromDate(e.target.value)}
              size='sm'
              type='date'
              // slotProps={{
              //   input: {
              //     min: '2018-06-07',
              //     max: '2018-06-14',
              //   },
              // }}
            />
          </Box>
          <Box>
            <Typography level='title-sm' component='label'>
              To Date
            </Typography>
            <Input
              size='sm'
              type='date'
              onChange={(e) => setToDate(e.target.value)}
              // slotProps={{
              //   input: {
              //     min: '2018-06-07',
              //     max: '2018-06-14',
              //   },
              // }}
            />
          </Box>
          <Button
            color='primary'
            loading={loading}
            onClick={getWorklogs}
            // startDecorator={<PostAddRoundedIcon />}
            size='sm'
            style={{ alignSelf: 'end' }}
          >
            Submit
          </Button>
          {mappedLogs && (
            <Button
              color='success'
              loading={loading}
              onClick={generateExcel}
              // startDecorator={<PostAddRoundedIcon />}
              size='sm'
              style={{ alignSelf: 'end' }}
            >
              Excel
            </Button>
          )}
          <Stack spacing={2} direction={'row'} sx={{ alignItems: 'flex-end', marginLeft: '5px' }}>
            {conflictIssues && conflictIssues.length > 0 && (
              <Item>
                <Link
                  style={{ alignSelf: 'end', fontSize: 14 }}
                  color='danger'
                  underline='none'
                  component='button'
                  onClick={() => setOpenConflictIssueModal(true)}
                >
                  {conflictIssues.length ?? 2} Conflicts
                </Link>
              </Item>
            )}
            {mappedLogs && totalInvoiceTime && (
              <Item>
                <Typography level='title-sm' component='label'>
                  Total Invoice Time: <strong>{getTimeLoggedStringFormat(totalInvoiceTime)}</strong>
                </Typography>
              </Item>
            )}
            {mappedLogs && (
              <Item>
                <Typography level='title-sm' component='label' style={{ display: 'block' }}>
                  Total Invoice Amount: <strong>{deutschNumberFormatter(calculateNewInvoiceTotalAmount(mappedLogs))} €</strong>
                </Typography>
              </Item>
            )}
          </Stack>
        </Box>
        {mappedLogs && mappedLogs.findIndex((x) => x.checked || x.book) >= 0 && (
          <Box
            sx={{
              display: 'flex',
              mb: 1,
              gap: 1,
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'start', sm: 'center' },
              flexWrap: 'wrap',
              alignSelf: 'end',
            }}
          >
            <Button
              color='primary'
              loading={submitting}
              onClick={() => onCreate()}
              startDecorator={<PostAddRoundedIcon />}
              size='sm'
              style={{ alignSelf: 'end' }}
            >
              Create
            </Button>
          </Box>
        )}
      </Box>
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          {mappedLogs && (
            <>
              <NewInvoiceTable
                mappedLogs={mappedLogs}
                onHourChange={updateHour}
                onMinuteChange={updateMinute}
                onOfferCheckedChange={updateOfferChecked}
                onOfferBookChange={updateOfferBook}
                onDateChange={updateOfferDate}
              />
              {/* <OrderList /> */}
            </>
          )}
        </>
      )}
      <InvoiceSubmitModal
        submitting={submitting}
        invoices={mappedLogInvoices!}
        open={openSubmitModal}
        onClose={closeInvoiceSubmitModal}
        onSubmit={onSubmit}
        onChangeInvoice={onChangeInvoice}
      />
      <ConflictIssuesModal
        closeModal={closeConflictIssueModal}
        open={openConflictIssueModal}
        conflictIssues={conflictIssues}
        onIgnore={onConflictIgnore}
      />
    </>
  );
};

export default NewInvoice;
